/**
 * Budget CSV imports (f10.10) -- the initial bulk load, through f8.1's
 * ingestion spine rather than a second upload path.
 *
 *   kind + drop -> parse job -> staged review (before -> after) -> accept
 *
 * Nothing is written to the budget tables until "Accept and commit"; the
 * commit job writes everything or nothing. Every figure, count and capability
 * on this panel is the server's.
 */

import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Check, FileSpreadsheet, History, Landmark, CalendarRange, UploadCloud, X } from 'lucide-react';

import type { BudgetImportKind, BudgetImportReview } from '@/api/client';
import {
  isJobTerminal,
  useBudgetImportJob,
  useBudgetImportReview,
  useBudgetImports,
  useCommitBudgetImport,
  useDiscardBudgetImport,
  useRefreshBudgets,
  useUploadBudgetImport,
} from '@/api/queries';
import { JobProgress } from '@/jobs/JobProgress';
import { formatBytes, formatWhen, shortChecksum, statePresentation } from '@/routes/data-import/importView';
import { Modal } from '@/routes/reference-data/Modal';
import { EmptyCell } from '@/ui/EmptyCell';

import { KIND_LABELS, MONTH_LABELS, actionPill, groupDigits, isParsing, refusalMessage, severityPill } from './budgetView';

interface WatchedJob {
  id: string;
  kind: 'parse' | 'commit';
}

const LAYOUTS: Readonly<Record<BudgetImportKind, { icon: typeof Landmark; columns: string }>> = {
  CLIENT_ANNUAL: { icon: Landmark, columns: 'Client · SAP ID (optional) · Year · Amount · Currency' },
  UNIT_MONTHLY: { icon: CalendarRange, columns: 'Business Unit · Year · Currency · Jan … Dec' },
};

export function BudgetImportsPanel() {
  const imports = useBudgetImports();
  const upload = useUploadBudgetImport();
  const [kind, setKind] = useState<BudgetImportKind>('CLIENT_ANNUAL');
  const [selected, setSelected] = useState<string | null>(null);
  const [watched, setWatched] = useState<WatchedJob | null>(null);
  const job = useBudgetImportJob(watched?.id);
  useJobCompletion(watched, job.data?.state);

  const batches = imports.data?.batches ?? [];
  const shown = batches.find((batch) => batch.id === selected) ?? batches.find((b) => b.state.code !== 'DISCARDED');
  const parsing = shown ? isParsing(shown.state.code) : false;
  const review = useBudgetImportReview(shown && !parsing ? shown.id : undefined);

  const startUpload = (file: File) =>
    upload.mutate(
      { kind, file },
      {
        onSuccess: (accepted) => {
          setSelected(accepted.batch_id);
          setWatched({ id: accepted.job_id, kind: 'parse' });
        },
      },
    );

  const jobCard = job.data && (!isJobTerminal(job.data.state) || job.data.state === 'FAILED') && (
    <div className="card">
      <div className="card-body">
        <JobProgress job={job.data} title={watched?.kind === 'commit' ? 'Storing the budget figures' : 'Reading the budget file'} />
      </div>
    </div>
  );

  return (
    <div className="budget-imports">
      <div className="stack">
        {upload.error ? <Alert message={refusalMessage(upload.error)} /> : null}
        <KindPicker kind={kind} onChange={setKind} />
        <Dropzone kind={kind} onFile={startUpload} busy={upload.isPending} />
        {jobCard}
        {shown && parsing && !jobCard && (
          <p className="hint" role="status">
            Reading {shown.original_filename ?? 'the uploaded file'}…
          </p>
        )}
        {review.isError && <Alert message={refusalMessage(review.error)} />}
        {review.data && (
          <ImportReview review={review.data}
            onCommitStarted={(jobId) => setWatched({ id: jobId, kind: 'commit' })} />
        )}
      </div>

      <aside className="card budget-history">
        <div className="card-head">
          <h3>
            <History aria-hidden="true" /> Recent imports
          </h3>
        </div>
        <div className="card-body">
          {imports.isPending && <p className="hint">Loading…</p>}
          {imports.isError && <Alert message={refusalMessage(imports.error)} />}
          {imports.data && batches.length === 0 && <p className="hint">No budget file has been uploaded yet.</p>}
          <ul className="budget-history-list">
            {batches.map((batch) => {
              const state = statePresentation(batch.state.code);
              return (
                <li key={batch.id}>
                  <button type="button" className={`budget-history-item${shown?.id === batch.id ? ' is-active' : ''}`}
                    onClick={() => setSelected(batch.id)}>
                    <span className="budget-history-name truncate">
                      #{batch.batch_number} · {batch.original_filename ?? 'budget file'}
                    </span>
                    <span className="hint">
                      {KIND_LABELS[batch.kind] ?? batch.kind} · {formatWhen(batch.uploaded_at)}
                    </span>
                    <span className={`badge ${state.pill}`}>{state.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </aside>
    </div>
  );
}

function useJobCompletion(watched: WatchedJob | null, state: string | undefined) {
  const refresh = useRefreshBudgets();
  const handled = useRef<string | null>(null);
  useEffect(() => {
    if (!watched || !isJobTerminal(state) || handled.current === watched.id) return;
    handled.current = watched.id;
    refresh();
  }, [watched, state, refresh]);
}

function KindPicker({ kind, onChange }: { kind: BudgetImportKind; onChange: (kind: BudgetImportKind) => void }) {
  return (
    <div className="budget-kinds" role="radiogroup" aria-label="What the file holds">
      {(Object.keys(LAYOUTS) as BudgetImportKind[]).map((option) => {
        const Icon = LAYOUTS[option].icon;
        return (
          <button key={option} type="button" role="radio" aria-checked={kind === option}
            className={`budget-kind${kind === option ? ' is-active' : ''}`} onClick={() => onChange(option)}>
            <Icon aria-hidden="true" />
            <span>
              <strong>{KIND_LABELS[option]}</strong>
              <span className="hint">{LAYOUTS[option].columns}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Dropzone({ kind, onFile, busy }: { kind: BudgetImportKind; onFile: (file: File) => void; busy: boolean }) {
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  return (
    <div
      className={`dropzone${over ? ' is-over' : ''}`}
      onDragOver={(event) => {
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        const file = event.dataTransfer.files.item(0);
        if (file) onFile(file);
      }}
    >
      <FileSpreadsheet className="dz-icon" aria-hidden="true" />
      <div className="dz-title">Drop a {(KIND_LABELS[kind] ?? kind).toLowerCase()} budget CSV</div>
      <div className="hint">
        Columns are bound by header name in any order; preamble rows are skipped. Nothing is written until you
        accept the preview.
      </div>
      <div className="btn-row dz-actions">
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => input.current?.click()}>
          <UploadCloud aria-hidden="true" /> Choose file
        </button>
      </div>
      <div className="hint">The format is detected from the file&rsquo;s content: only CSV is accepted</div>
      <input
        ref={input}
        type="file"
        className="sr-only"
        aria-label="Budget file"
        onChange={(event) => {
          const file = event.target.files?.item(0);
          if (file) onFile(file);
          event.target.value = '';
        }}
      />
    </div>
  );
}

function ImportReview({ review, onCommitStarted }: { review: BudgetImportReview; onCommitStarted: (jobId: string) => void }) {
  const commit = useCommitBudgetImport(review.batch.id);
  const discard = useDiscardBudgetImport(review.batch.id);
  const [confirming, setConfirming] = useState(false);
  const { batch, issues, figures, capabilities } = review;
  const state = statePresentation(batch.state.code);
  const writes = figures.create + figures.update;

  return (
    <>
      <div className="card">
        <div className="card-head">
          <h3>File dropped</h3>
          <span className={`badge ${state.pill}`}>{state.label}</span>
        </div>
        <div className="card-body">
          <dl className="dl cols-4">
            <div>
              <dt>File</dt>
              <dd className="truncate">{batch.original_filename ?? <EmptyCell />}</dd>
            </div>
            <div>
              <dt>Layout</dt>
              <dd>{KIND_LABELS[batch.kind] ?? batch.kind}</dd>
            </div>
            <div>
              <dt>Detected format</dt>
              <dd>
                {batch.detected_format} <span className="hint">sniffed by content</span>
              </dd>
            </div>
            <div>
              <dt>Rows parsed / kept / dropped</dt>
              <dd>
                {batch.rows_in ?? '—'} / {batch.rows_out ?? '—'} / {batch.rows_dropped ?? '—'}
              </dd>
            </div>
            <div>
              <dt>Header row</dt>
              <dd>{batch.detected_header_row ?? <EmptyCell />}</dd>
            </div>
            <div>
              <dt>Size</dt>
              <dd>{formatBytes(batch.byte_size)}</dd>
            </div>
            <div>
              <dt>Checksum</dt>
              <dd className="mono">{shortChecksum(batch.content_sha256)}</dd>
            </div>
            <div>
              <dt>Uploaded</dt>
              <dd>
                {formatWhen(batch.uploaded_at)}
                {batch.uploaded_by ? ` · ${batch.uploaded_by.name}` : ''}
              </dd>
            </div>
          </dl>
          {batch.rejection_reason && <Alert message={`Rejected: ${batch.rejection_reason}`} />}
        </div>
      </div>

      {issues.items.length > 0 && (
        <div className="card">
          <div className="card-head">
            <h3>What the system did with it</h3>
            <span className="hint">
              {issues.counts.ERROR ?? 0} error · {issues.counts.WARNING ?? 0} warning · {issues.counts.INFO ?? 0} info
            </span>
          </div>
          <ul className="card-body budget-issues">
            {issues.items.map((issue, index) => (
              <li key={`${issue.code}-${issue.source_row_number ?? 'b'}-${index}`}>
                <span className={severityPill(issue.severity)}>{issue.severity}</span>
                <span className="mono hint">{issue.source_row_number ? `row ${issue.source_row_number}` : 'file'}</span>
                <span>{issue.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <h3>Resulting figures</h3>
          <div className="budget-chips">
            <span className="badge pill-success">{figures.create} create</span>
            <span className="badge pill-info">{figures.update} update</span>
            <span className="badge pill-neutral">{figures.unchanged} unchanged</span>
            <span className="badge pill-danger">{figures.invalid} invalid</span>
          </div>
        </div>
        {review.rows.length === 0 ? (
          <div className="card-body">
            <p className="hint">No figure was staged from this file.</p>
          </div>
        ) : (
          <div className="table-scroll budget-review-scroll">
            <table className="data compact budget-table">
              <thead>
                <tr>
                  <th className="num">Row</th>
                  <th>Target</th>
                  <th>Year</th>
                  <th>Month</th>
                  <th className="num">Before → after</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {review.rows.map((row, index) => (
                  <tr key={`${row.source_row_number}-${row.month ?? 0}-${index}`}>
                    <td className="num mono">{row.source_row_number}</td>
                    <td>{row.target.name ?? row.target.raw ?? <EmptyCell />}</td>
                    <td className="mono">{row.fiscal_year ?? <EmptyCell />}</td>
                    <td>{row.month ? MONTH_LABELS[row.month - 1] : <EmptyCell />}</td>
                    <td className="num budget-amount">
                      {row.current_amount !== null && (
                        <span className="budget-before">
                          {groupDigits(row.current_amount)} {row.current_currency_code}
                          <ArrowRight aria-hidden="true" />
                        </span>
                      )}
                      {row.amount !== null ? `${groupDigits(row.amount)} ${row.currency_code ?? ''}` : row.amount_raw ?? ''}
                    </td>
                    <td>
                      <span className={actionPill(row.action.code)}>{row.action.name}</span>
                      {row.message && <div className="hint">{row.message}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(capabilities.can_commit || capabilities.can_discard) && (
          <div className="card-foot">
            <span className="hint">
              {capabilities.can_commit
                ? `Accepting writes ${writes} figure${writes === 1 ? '' : 's'} in one transaction, or none.`
                : 'An invalid row blocks the commit — discard this import and upload a corrected file.'}
            </span>
            <div className="btn-row">
              {capabilities.can_discard && (
                <button type="button" className="btn btn-secondary" disabled={discard.isPending}
                  onClick={() => discard.mutate()}>
                  <X aria-hidden="true" /> Discard
                </button>
              )}
              <button type="button" className="btn btn-primary" disabled={!capabilities.can_commit}
                onClick={() => setConfirming(true)}>
                <Check aria-hidden="true" /> Accept and commit
              </button>
            </div>
          </div>
        )}
      </div>
      {discard.error ? <Alert message={refusalMessage(discard.error)} /> : null}

      {confirming && (
        <Modal
          title="Commit this budget import?"
          subtitle={`${figures.create} new and ${figures.update} changed figures; ${figures.unchanged} unchanged are skipped.`}
          narrow
          layout="plain"
          busy={commit.isPending}
          submitLabel="Accept and commit"
          submitIcon={<Check aria-hidden="true" />}
          message={commit.error ? refusalMessage(commit.error) : null}
          onClose={() => {
            setConfirming(false);
            commit.reset();
          }}
          onSubmit={() =>
            commit.mutate(undefined, {
              onSuccess: (accepted) => {
                setConfirming(false);
                onCommitStarted(accepted.job_id);
              },
            })
          }
        >
          <p className="hint">
            Every figure is written with this import as its source, and the change is recorded in the audit log.
            If a client or unit was deactivated since the preview, nothing is written.
          </p>
        </Modal>
      )}
    </>
  );
}

function Alert({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="alert alert-danger" role="alert">
      <div className="a-body">{message}</div>
    </div>
  );
}
